// Shows all possible placements for each top-performing Google ad, as per placement
// performance breakdown is not available -> can't determine which placement actually performed best.

import {
  getDisplay9x16Variations,
  getDisplay1x1Variations,
  getDisplay191x1Variations,
  getDisplayVideoVariations,
  getDisplayGeneralVariations,
} from './googleDisplayVariations'
import {
  getDemandGen9x16Variations,
  getDemandGen1x1Variations,
  getDemandGen191x1Variations,
  getDemandGen4x5Variations,
} from './googleDemandGenVariations'
import { GOOGLE_DISPLAY_PLACEMENTS, GOOGLE_DEMAND_GEN_PLACEMENTS } from './placementGroups'

const DEFAULT_AD_TYPE = 'DemandGenMultiAssetAdConfig'

const DISPLAY_GROUP_BUILDERS = {
  'google-display-9x16': getDisplay9x16Variations,
  'google-display-1x1': getDisplay1x1Variations,
  'google-display-191x1': getDisplay191x1Variations,
  'google-display-video': getDisplayVideoVariations,
  'google-display-general': getDisplayGeneralVariations,
}

const DEMAND_GEN_GROUP_BUILDERS = {
  'google-demand-gen-9x16': getDemandGen9x16Variations,
  'google-demand-gen-1x1': getDemandGen1x1Variations,
  'google-demand-gen-191x1': getDemandGen191x1Variations,
  'google-demand-gen-4x5': getDemandGen4x5Variations,
}

// fall back to single-card path for carousel ads
const AD_TYPE_GROUPS = {
  DisplayAdConfig: [GOOGLE_DISPLAY_PLACEMENTS, DISPLAY_GROUP_BUILDERS],
  DemandGenMultiAssetAdConfig: [GOOGLE_DEMAND_GEN_PLACEMENTS, DEMAND_GEN_GROUP_BUILDERS],
  DemandGenVideoAdConfig: [GOOGLE_DEMAND_GEN_PLACEMENTS, DEMAND_GEN_GROUP_BUILDERS],
  DemandGenCarouselAdConfig: [[], {}],
}

// Expand image bundles
export function topAdMediaList(media) {
  if (!media) return []
  if (Array.isArray(media)) return [] // carousel card list — no carousel skin exists

  // Demand Gen multi-asset bundle (auto_placement_media).
  const demandGenSlots = [
    media.marketing_image,
    media.square_marketing_image,
    media.portrait_marketing_image,
    media.tall_portrait_marketing_image,
  ].filter(Boolean)
  if (demandGenSlots.length) return demandGenSlots

  // Display bundle (media_bundles): a landscape/square image pair, plus an
  // optional YouTube id carried as a bare string rather than a media item.
  if (media.landscape_image || media.square_image) {
    const displaySlots = [media.landscape_image, media.square_image].filter(Boolean)
    if (media.youtube_video_id) {
      displaySlots.push({ type: 'video', youtube_video_id: media.youtube_video_id })
    }
    return displaySlots
  }

  // Demand Gen video bundle (video_bundles): ids only, no image slots.
  if (media.youtube_video_ids) {
    return media.youtube_video_ids.map((id) => ({ type: 'video', youtube_video_id: id }))
  }

  return media.url || media.youtube_video_id ? [media] : []
}

export function topAdToCreative(item) {
  return {
    headline: [item.headline ?? ''],
    description: item.description ?? '',
    long_headline: [item.longHeadline ?? ''],
    destination_url: [item.destinationUrl ?? ''],
    display_url_path: [item.displayUrlPath ?? null],
    call_to_action: item.callToAction || 'LEARN_MORE',
    business_name: item.businessName ?? '',
    logo: item.logo ?? null,
    companion_banner: item.companionBanner ?? null,
    media: topAdMediaList(item.media),
  }
}

// Avoid repeating/grouping the same skin together in the carousel previews.
function pickRepresentative(ads, usedSkins) {
  return ads.find((ad) => !usedSkins.has(ad.skin)) ?? ads[0]
}

export function getGoogleTopAdPreviews(item, t) {
  const [placements, builders] = AD_TYPE_GROUPS[item.adType ?? DEFAULT_AD_TYPE] ?? []
  if (!placements?.length) return []

  const creative = topAdToCreative(item)
  const usedSkins = new Set()
  const previews = []

  placements.forEach((placement) => {
    const ads = builders[placement.key]?.(creative, t) ?? []
    if (!ads.length) return
    const ad = pickRepresentative(ads, usedSkins)
    usedSkins.add(ad.skin)
    previews.push({ key: placement.key, placement: placement.name, ad })
  })

  return previews
}
